/**
 * Extracts numeric ID from a standard IRI format.
 *
 * @param {string} iri - The IRI string (e.g., "/api/resource/123")
 * @returns {number} The extracted numeric ID or NaN if extraction failed
 */
function extractStandardFormatId(iri: string): number {
    const parts = iri.split('/');
    // Typically, the ID is the last segment in the path
    const lastSegment = parts[parts.length - 1];
    return Number.parseInt(lastSegment, 10);
}

/**
 * Extracts numeric ID from a child entity reference format.
 *
 * @param {string} reference - The reference string (e.g., "_:1234")
 * @returns {number} The extracted numeric ID or NaN if extraction failed
 */
function extractChildReferenceId(reference: string): number {
    const childIdPattern = /^_:(\d+)$/;
    const match = reference.match(childIdPattern);

    if (match && match[1]) {
        return Number.parseInt(match[1], 10);
    }

    return NaN;
}

/**
 * Extracts numeric ID from a JSON-LD identifier string.
 *
 * Handles different formats:
 * - Standard IRI format ('/api/resource/123')
 * - Child entity reference format ('_:1234')
 *
 * @param {string} idToScrap - The JSON-LD identifier string to extract ID from
 * @returns {number} The extracted numeric ID or NaN if extraction failed
 * @throws {Error} If the provided input is not a string
 */
export default function jsonLdIdScraper(idToScrap: string): number {
    if (typeof idToScrap !== 'string') {
        throw new Error('The provided input must be a string');
    }

    // Try to extract ID from standard IRI format (e.g., "/api/resource/123")
    const standardFormatId = extractStandardFormatId(idToScrap);
    if (!Number.isNaN(standardFormatId)) {
        return standardFormatId;
    }

    // Try to extract ID from child entity reference format (e.g., "_:1234")
    const childReferenceId = extractChildReferenceId(idToScrap);
    if (!Number.isNaN(childReferenceId)) {
        return childReferenceId;
    }

    // If both methods fail, return NaN
    return NaN;
}
